import { OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaObserver } from '@angular/flex-layout';
import { Subject, combineLatest } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { ApplicationService, RequestInfo, SchoolInfo, CategoryInfo, FileAttachmentInfo, PersonInfo, RequestCommentInfo, SearchParameters, } from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { DownloadService, HttpMethod, DownloadFormat } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';
import { RequestIssuePipe } from 'pipes/requestissue.pipe';
var ViewRequestComponent = /** @class */ (function () {
    function ViewRequestComponent(appSvc, configSvc, dlgSvc, downloadSvc, errorSvc, fb, http, media, requestIssuePipe, route, router, splashSvc, userSvc) {
        this.appSvc = appSvc;
        this.configSvc = configSvc;
        this.dlgSvc = dlgSvc;
        this.downloadSvc = downloadSvc;
        this.errorSvc = errorSvc;
        this.fb = fb;
        this.http = http;
        this.media = media;
        this.requestIssuePipe = requestIssuePipe;
        this.route = route;
        this.router = router;
        this.splashSvc = splashSvc;
        this.userSvc = userSvc;
        this._subrelease = new Subject();
    }
    ViewRequestComponent.prototype.ngOnDestroy = function () {
        this._subrelease.next();
        this._subrelease.complete();
    };
    ViewRequestComponent.prototype.ngOnInit = function () {
        var self = this;
        self.AllCategories = null;
        self.AllSchools = null;
        self.AsBoard = true;
        self.BoardMembers = null;
        self.CommentsData = new MatTableDataSource([{}]);
        // Set a flag if this is an iPad device, since those cannot do file attachment uploads
        self.IsiPad = window.navigator.userAgent.indexOf('iPad') >= 0 || window.navigator.userAgent.indexOf('iPhone') >= 0;
        self.CommentsNoData = false;
        self.CommentsVisibleColumns = [];
        self.CommentsVisibleHeaders = [];
        self.SelectedTabIndex = 0;
        self.UnableToView = false;
        self.User = null;
        self.ViewReady = false;
        // Wait for the first non-null user info object
        self.userSvc.UserSubject.pipe(takeUntil(self._subrelease), first(function (_u) { return !!_u && !!_u.AuthToken; })).subscribe(function (_user) {
            self.User = _user;
            self.AsBoard = self.User.CurrentRole === 'BoardMember' || self.User.CurrentRole === 'BoardAssistant';
            // Subscribe to constant user changes, to keep AuthToken in sync.
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(function (user) {
                self.User = user;
                self.AsBoard = self.User
                    ? self.User.CurrentRole === 'BoardMember' || self.User.CurrentRole === 'BoardAssistant'
                    : true;
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
            // Subscribe to media size changes.
            self.media.media$.pipe(takeUntil(self._subrelease)).subscribe(function (m) {
                self.SmallSize = m.mqAlias === 'xs' || m.mqAlias === 'sm';
                self.CommentsVisibleHeaders = self.SmallSize
                    ? ['SmallSize']
                    : ['DateCommentAdded', 'Comment', 'CommentCreatorFullName', 'CommentRecipientFullName'];
                self.CommentsVisibleColumns = self.CommentsNoData ? ['NoData'] : self.CommentsVisibleHeaders.slice();
            });
            // Load data for component
            combineLatest(self.appSvc.GetAllSchools(), self.appSvc.GetAllCategories(), self.appSvc.GetAllBoardMembers(), self.route.params)
                .pipe(takeUntil(self._subrelease), map(function (_p) {
                return {
                    Schools: _p[0],
                    Categories: _p[1],
                    Board: _p[2],
                    Params: _p[3],
                };
            }))
                .subscribe(function (packet) {
                self.AllSchools = packet.Schools || [];
                self.AllCategories = packet.Categories || [];
                self.BoardMembers = packet.Board || [];
                self.ParamsRequestID = +packet.Params.RequestID;
                self.ParamsPreviousPage = packet.Params.PreviousPage;
                // If we have a request ID, load it now so we can populate the form with the data from the request.
                if (self.ParamsRequestID != null) {
                    self.appSvc
                        .GetRequest(self.User.AuthToken, self.User.CurrentRole, self.ParamsRequestID, true)
                        .subscribe(function (request) {
                        // Update the form with the loaded request
                        self.LoadedRequest = request;
                        self.CanFollowUp =
                            self.LoadedRequest &&
                                self.LoadedRequest.FollowUpCount === 0 &&
                                ['COMPLETED', 'UNAVAIL', 'DONEWINFO', 'DONENOINFO'].includes(self.LoadedRequest.Status);
                        self.LoadedRequest.ConstituentPrivate =
                            self.LoadedRequest.ConstituentPrivate != null
                                ? self.LoadedRequest.ConstituentPrivate
                                : true;
                        // Set the current request
                        self.appSvc.CurrentRequest = {
                            FiscalYear: self.LoadedRequest.FiscalYear,
                            RequestID: self.LoadedRequest.RequestID,
                            IssueNumber: self.LoadedRequest.RequestIssueNumber,
                        };
                        // Set up the comments data source
                        self.CommentsData.data = request.Comments || [];
                        self.CommentsNoData = !self.CommentsData.data || !self.CommentsData.data.length;
                        self.CommentsVisibleHeaders = self.SmallSize
                            ? ['SmallSize']
                            : [
                                'DateCommentAdded',
                                'Comment',
                                'CommentCreatorFullName',
                                'CommentRecipientFullName',
                            ];
                        self.CommentsVisibleColumns = self.CommentsNoData
                            ? ['NoData']
                            : self.CommentsVisibleHeaders.slice();
                        self.ViewReady = true;
                    }, function (error) {
                        // Is this an unauthorized access? If so, display a kinder message. Otherwise, fail out with an error splash.
                        var errorInfo = self.errorSvc.RetrieveServiceError(error);
                        if (errorInfo.ErrorMessage.startsWith('User is not authorized to view') ||
                            errorInfo.ErrorMessage.includes('could not be located in the database')) {
                            self.UnableToView = true;
                        }
                        else {
                            self.errorSvc.HandleServiceError(error);
                        }
                        self.ViewReady = true;
                    });
                }
                else {
                    // Show the form with nothing loaded
                    self.ViewReady = true;
                }
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
        }, function (error) { return self.errorSvc.HandleServiceError(error); });
    };
    // #region Handle Cancel Request
    ViewRequestComponent.prototype.HandleCancelRequest = function () {
        var self = this;
        if (!self.LoadedRequest) {
            return;
        }
        // Get a confirmation before proceeding
        self.dlgSvc
            .OpenConfirmationDialog({
            TitleText: 'Cancellation Confirmation Requested',
            MessageText: "You are about to cancel this request in the IRIS system:<br /><br />" +
                ("<strong>Subject: </strong>" + self.LoadedRequest.Subject + "<br /><br />Once cancelled, this ") +
                "request cannot be reactivated. Are you sure you wish to proceed?",
            OKText: 'Yes - Cancel Request',
            CancelText: 'No - Do Nothing',
        })
            .subscribe(function (result) {
            if (result === true) {
                // User agreed. Cancel this request by setting the status code to 'CANCEL' and setting a default message.
                self.LoadedRequest.Status = 'CANCEL';
                self.LoadedRequest.RequestCloseNote = 'This request was cancelled in the system. No further action is necessary.';
                self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Cancelling request...' }).subscribe(function () {
                    self.appSvc.RequestCancel(self.User.AuthToken, self.User.CurrentRole, self.LoadedRequest.RequestID).subscribe(function (updatedRequest) {
                        // Drop splash and snackbar success
                        self.splashSvc.CloseSplash();
                        self.splashSvc.SnackbarSuccess("Request #" + self.requestIssuePipe.transform(self.LoadedRequest.RequestIssueNumber, self.LoadedRequest.FiscalYear) + " Cancelled Successfully.");
                        // If there is a known previous page, return to it now.
                        if (self.ParamsPreviousPage) {
                            self.router.navigate([self.ParamsPreviousPage]);
                        }
                    }, function (error) { return self.errorSvc.HandleServiceError(error); });
                });
            }
        });
    };
    // #endregion
    // #region Handle File Attachment
    ViewRequestComponent.prototype.HandleFileAttachment = function (file) {
        if (!file) {
            return;
        }
        var self = this;
        // Download the selected file
        self.splashSvc.OpenSplash({ TitleText: 'Please Wait', MessageText: 'Downloading file...' }).subscribe(function () {
            self.downloadSvc
                .RetrieveDownload(HttpMethod.GET, "Api/Services/IRIS/DownloadFile", {
                AuthToken: self.User.AuthToken || '',
                FileName: file.FileName,
                FilePath: file.SharepointURL,
            }, DownloadFormat.Any)
                .subscribe(function () {
                self.splashSvc.CloseSplash();
            }, function (error) { return self.errorSvc.HandleServiceError(error); });
        });
    };
    // #endregion
    // #region Handle Search Return
    ViewRequestComponent.prototype.HandleSearchReturn = function () {
        var self = this;
        // Navigate back to the search page
        self.router.navigate(['search']);
    };
    // #endregion
    // #region Request FollowUp
    ViewRequestComponent.prototype.RequestFollowUp = function () {
        var self = this;
        if (!self.CanFollowUp) {
            return;
        }
        // Pull the text of the followup question.
        self.dlgSvc
            .OpenTextNoteDialog({
            TitleText: 'Follow-Up Request',
            MessageText: "Please enter the follow-up text/question you have regarding this " +
                "completed request. This follow-up text/question will be added to the request and the " +
                "request will be returned to the district staff member who responded to this " +
                "request. You will be notified by the system when your follow-up text/question " +
                "has been responded to.",
            OKText: 'Done',
            CancelText: 'Cancel',
            TextLimit: 5000,
        })
            .subscribe(function (note) {
            if (note == null || !note.Message) {
                return;
            }
            // Get a confirmation before action.
            self.dlgSvc
                .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: "You are about to send the following follow-up text/question " +
                    "to the district staff member who responded to this request:<br /><br />" +
                    ("<em>" + note.Message + "</em><br /><br />Once you click the \"Send Follow-Up\" button, ") +
                    "this request will be returned to the district staff member for a follow-up " +
                    "response. Are you ready to proceed?",
                OKText: 'Yes - Send Follow-Up',
                CancelText: 'No - Cancel',
            })
                .subscribe(function (_) {
                if (_ == null) {
                    return;
                }
                // Send off to the server for handling.
                self.splashSvc
                    .OpenSplash({
                    TitleText: 'Please Wait',
                    MessageText: 'Sending Follow-Up...',
                })
                    .subscribe(function (__) {
                    // Off load to the server.
                    self.appSvc
                        .AddFollowUpToRequest(self.User.AuthToken, self.User.CurrentRole, self.LoadedRequest.RequestID, note.Message)
                        .subscribe(function (result) {
                        // Toast success.
                        self.splashSvc.CloseSplash();
                        self.splashSvc.SnackbarSuccess('Request Follow-Up Sent Successfully.');
                        // Redirect to the previous page, if we know it. Otherwise, go back to the home route.
                        self.router.navigate([self.ParamsPreviousPage || 'home']);
                    }, function (error) { return self.errorSvc.HandleServiceError(error); });
                });
            });
        });
    };
    return ViewRequestComponent;
}());
export { ViewRequestComponent };
